import { useTranslation } from 'react-i18next'
import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { PlusIcon, XMarkIcon, EyeIcon, PhotoIcon, TrashIcon } from '@heroicons/react/24/outline'
import { format } from 'date-fns'
import api from '@/api/client'

interface SightingForm {
  speciesName: string
  diveDate: string
  depthM: number | ''
  notes: string
  siteName: string
}

async function uploadPhoto(file: File): Promise<string> {
  const fd = new FormData()
  fd.append('file', file)
  fd.append('consent', 'true')
  const res = await api.post('/v1/divesites/media/upload', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return res.data.object_key as string
}

async function createSighting(data: {
  species_name: string
  dive_date: string
  depth_m?: number
  notes?: string
  image_key?: string
}) {
  return api.post('/v1/sightings/', data).then(r => r.data)
}

export default function FaunaReportFAB() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [photoFile, setPhotoFile] = useState<File | null>(null)
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)
  const [uploadProgress, setUploadProgress] = useState<'idle' | 'uploading' | 'done'>('idle')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const qc = useQueryClient()
  const today = format(new Date(), 'yyyy-MM-dd')

  const { register, handleSubmit, reset, formState: { errors } } = useForm<SightingForm>({
    defaultValues: { speciesName: '', diveDate: today, depthM: '', notes: '', siteName: '' },
  })

  const mutation = useMutation({
    mutationFn: async (data: SightingForm) => {
      let imageKey: string | undefined
      if (photoFile) {
        setUploadProgress('uploading')
        try {
          imageKey = await uploadPhoto(photoFile)
          setUploadProgress('done')
        } catch {
          setUploadProgress('idle')
          throw new Error('Caricamento foto fallito. Riprova.')
        }
      }

      const noteParts = []
      if (data.siteName) noteParts.push(`Sito: ${data.siteName}`)
      if (data.notes) noteParts.push(data.notes)

      return createSighting({
        species_name: data.speciesName,
        dive_date: data.diveDate,
        ...(data.depthM !== '' ? { depth_m: Number(data.depthM) } : {}),
        ...(noteParts.length ? { notes: noteParts.join(' — ') } : {}),
        ...(imageKey ? { image_key: imageKey } : {}),
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sightings'] })
      toast.success(t('auto.componentsFaunaReportFAB.avvistamentoRegistratoSaraRevisionatoDalCentro'))
      reset()
      clearPhoto()
      setOpen(false)
    },
    onError: (err: any) => toast.error(err?.message ?? 'Errore durante la segnalazione'),
  })

  function clearPhoto() {
    setPhotoFile(null)
    setPhotoPreview(null)
    setUploadProgress('idle')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 50 * 1024 * 1024) { toast.error(t('auto.componentsFaunaReportFAB.fotoTroppoGrandeMax50Mb')); return }
    setPhotoFile(file)
    const reader = new FileReader()
    reader.onload = ev => setPhotoPreview(ev.target?.result as string)
    reader.readAsDataURL(file)
  }

  function handleClose() {
    setOpen(false)
    reset()
    clearPhoto()
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-20 right-4 lg:bottom-6 lg:right-6 z-30 w-14 h-14 rounded-full bg-emerald-600 hover:bg-emerald-500 active:scale-95 text-white shadow-lg flex items-center justify-center transition-all"
        aria-label={t('auto.componentsFaunaReportFAB.segnalaAvvistamentoFauna')}
      >
        <PlusIcon className="w-7 h-7" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 px-3 pb-3 sm:pb-0">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-md max-h-[92dvh] flex flex-col">

            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-200 shrink-0">
              <div className="flex items-center gap-2">
                <EyeIcon className="w-5 h-5 text-emerald-600" />
                <span className="font-semibold text-gray-900">{t('auto.componentsFaunaReportFAB.segnalaAvvistamento')}</span>
              </div>
              <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Scrollable body */}
            <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="flex flex-col flex-1 overflow-hidden">
            <div className="p-5 space-y-4 overflow-y-auto flex-1">

              {/* Photo upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Foto dell'avvistamento
                  <span className="ml-2 font-normal text-gray-400 text-xs">{t('auto.componentsFaunaReportFAB.consigliataPerLaClassificazione')}</span>
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={handleFileChange}
                  capture="environment"
                />
                {!photoPreview ? (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-28 border-2 border-dashed border-emerald-300 rounded-xl flex flex-col items-center justify-center gap-2 text-emerald-600 hover:bg-emerald-50 transition-colors"
                  >
                    <PhotoIcon className="w-8 h-8" />
                    <span className="text-sm font-medium">{t('auto.componentsFaunaReportFAB.scattaOCaricaUnaFoto')}</span>
                    <span className="text-xs text-gray-400">{t('auto.componentsFaunaReportFAB.jpegPngWebpMax50Mb')}</span>
                  </button>
                ) : (
                  <div className="relative rounded-xl overflow-hidden border border-gray-200">
                    <img src={photoPreview} alt={t('auto.componentsFaunaReportFAB.anteprima')} className="w-full h-40 object-cover" />
                    <button
                      type="button"
                      onClick={clearPhoto}
                      className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1 hover:bg-black/80"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                    {uploadProgress === 'uploading' && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white text-sm font-medium">{t('auto.componentsFaunaReportFAB.caricamentoInCorso')}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Species name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Specie <span className="text-red-500">*</span>
                  <span className="ml-2 font-normal text-gray-400 text-xs">{t('auto.componentsFaunaReportFAB.seSconosciutaDescriviLAspetto')}</span>
                </label>
                <input
                  {...register('speciesName', { required: 'Inserisci un nome o descrizione' })}
                  placeholder={t('auto.componentsFaunaReportFAB.esCerniaBrunaOppurePesceRosso')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900"
                />
                {errors.speciesName && <p className="text-red-500 text-xs mt-1">{errors.speciesName.message}</p>}
              </div>

              {/* Date + depth */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('auto.componentsFaunaReportFAB.data')} <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    {...register('diveDate', { required: true })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('auto.componentsFaunaReportFAB.profonditaM')}</label>
                  <input
                    type="number" min={0} max={400} step={0.5}
                    {...register('depthM')}
                    placeholder={t('auto.componentsFaunaReportFAB.es12')}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900"
                  />
                </div>
              </div>

              {/* Site */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('auto.componentsFaunaReportFAB.sitoLocalita')}</label>
                <input
                  {...register('siteName')}
                  placeholder={t('auto.componentsFaunaReportFAB.esTorreGuacetoIsolaDElba')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('auto.componentsFaunaReportFAB.note')}</label>
                <textarea
                  {...register('notes')}
                  rows={2}
                  placeholder={t('auto.componentsFaunaReportFAB.comportamentoColoreDimensioniNumeroDiEsemplari')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none text-gray-900"
                />
              </div>

              <p className="text-xs text-gray-400 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                {t('auto.componentsFaunaReportFAB.leSpecieNonIdentificateVengonoInviate')}
                Caricando la foto dichiari di possederne i diritti e autorizzi eDive alla pubblicazione.
              </p>

            </div>
            {/* Sticky submit footer */}
            <div className="shrink-0 px-5 py-3 border-t border-gray-200 flex gap-3 bg-white"
                 style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)' }}>
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 active:bg-gray-100"
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={mutation.isPending}
                className="flex-1 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {mutation.isPending ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                    </svg>
                    {uploadProgress === 'uploading' ? 'Caricamento foto…' : 'Salvataggio…'}
                  </>
                ) : 'Segnala avvistamento'}
              </button>
            </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}
